import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Breadcrumb, Row, Col, Button, message, Modal, Icon, Tooltip } from 'antd';
import { makeStyles } from '@material-ui/styles';

import { getItem } from '~helpers/storage';
import generatePath from '~settings/routing';
import UpdateLandlordStatusBtn from '../../updateLandlordStatus';
import { isSupplyRole, isAdminRole, isRegionalSupplyHeadRole } from '~helpers/auth';
import { fetch } from '~helpers/graphql';
import * as queries from '~settings/queries';

const useStyles = makeStyles(() => ({
  statusTag: () => ({
    display: 'inline-block',
    lineHeight: '22px',
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.45)',
  }),
  headerRightWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  statusWrapper: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    padding: '5px 20px',
  },
  statusIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  deleteBtn: {
    border: '1px solid #D9D9D9',
    fontSize: 14,
    lineHeight: '22px',
    color: 'rgba(0, 0, 0, 0.65)',
    marginLeft: 12,
    cursor: 'pointer',
    padding: '5px 16px',
    borderRadius: 16,
    '&:hover': {
      borderColor: '#FF4D4F',
      color: '#FF4D4F',
    },
  },
  deleteBtnDisabled: {
    backgroundColor: '#f5f5f5',
    borderColor: '#D9D9D9',
    color: 'rgba(0, 0, 0, 0.25)',
    cursor: 'not-allowed',
    '&:hover': {
      borderColor: '#D9D9D9',
      color: 'rgba(0, 0, 0, 0.25)',
    },
  },
  statusText: {
    fontSize: 14,
    lineHeight: '22px',
    color: 'rgba(0, 0, 0, 0.85)',
    marginRight: 16,
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',

    '&.status-tag-pending::before': {
      content: '""',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#f9a600',
    },
    '&.status-tag-verified::before': {
      content: '""',
      display: 'inline-block',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#3bc56f',
    },
    '&.status-tag-rejected::before': {
      content: '""',
      display: 'inline-block',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#ff4d4f',
    },
    '&.status-tag-lost::before': {
      content: '""',
      display: 'inline-block',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#8c8c8c',
    },
    '&.status-tag-unpublished::before': {
      content: '""',
      display: 'inline-block',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#bfbfbf',
    },
    '&.status-tag-onboarding::before': {
      content: '""',
      display: 'inline-block',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#99b9ff',
    },
    '&.status-tag-in_consultation::before': {
      content: '""',
      display: 'inline-block',
      height: 6,
      width: 6,
      borderRadius: 3,
      marginRight: 6,
      backgroundColor: '#40a9ff',
    },
  },
}));
const formatStatusStr = str =>
  str
    ?.split('_')
    ?.map(item => capitalize(item))
    ?.join(' ');

const LandlordHeader = ({ landlord, getLandlord }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);

  const classes = useStyles({
    status: landlord.status,
  });

  const name =
    landlord?.businessType === 'INDIVIDUAL'
      ? `${landlord?.firstName} ${landlord?.lastName}`
      : landlord?.name;

  const handleDeleteLandlord = () => {
    fetch(
      queries.deleteLandlord({
        landlordId: landlord?.id,
      }),
    )
      .then(res => {
        if (res?.deleteLandlord?.success) {
          history.replace(generatePath('landlord'));
        } else {
          message.error(t('cms.properties.edit.toast.error'));
        }
      })
      .catch(() => {
        message.error(t('cms.properties.edit.toast.error'));
      })
      .finally(() => {
        setVisibleDeleteModal(false);
      });
  };

  return (
    <div className="landlord-header">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to={generatePath('landlords', {}, getItem('cms_landlords_list_filters'))}>
            {t('cms.landlord.detail.landlord_list.breadcrumb')}
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{t('cms.landlord.detail.landlord_details.breadcrumb')}</Breadcrumb.Item>
      </Breadcrumb>
      <Row type={'flex'} justify={'space-between'} align={'middle'}>
        <Col>
          <h2 className="landlord-header__name">{name}</h2>
        </Col>

        <div className={classes.headerRightWrapper}>
          <If condition={isSupplyRole() || isAdminRole() || isRegionalSupplyHeadRole()}>
            <div className={classes.statusWrapper}>
              <div className={classes.statusIcon}>
                {landlord.status && (
                  <>
                    <span className={classes.statusTag}>
                      {t('cms.landlord.detail.header.status')}
                    </span>
                    <span
                      className={`${
                        classes.statusText
                      } status-tag-${landlord.status.toLowerCase()}`}
                    >
                      {formatStatusStr(landlord.status)}
                    </span>
                  </>
                )}
              </div>
              <UpdateLandlordStatusBtn
                btnType="primary"
                landlordId={landlord.id}
                landlordStatus={landlord.status}
                successCallBack={getLandlord}
              />
            </div>
          </If>
          <Tooltip
            placement="bottomRight"
            arrowPointAtCenter
            title={
              landlord.properties?.totalCount > 0
                ? t('cms.property_management.delete.landlord_has_property.desc')
                : null
            }
          >
            <div
              className={[
                classes.deleteBtn,
                landlord.properties?.totalCount > 0 && classes.deleteBtnDisabled,
              ]}
              onClick={() => {
                if (landlord.properties?.totalCount === 0) {
                  setVisibleDeleteModal(true);
                }
              }}
            >
              <span style={{ fontSize: 14 }}>{t('cms.form.button.delete')}</span>
            </div>
          </Tooltip>
        </div>
      </Row>
      <Modal
        visible={visibleDeleteModal}
        width={416}
        okText={t('cms.form.button.delete')}
        className="delete-modal-wrapper"
        centered
        closable={false}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setVisibleDeleteModal(false);
            }}
          >
            {t('cms.billing.transfer.cancel.btn')}
          </Button>,
          <Button
            key="submit"
            type="danger"
            style={{ marginLeft: 8, fontWeight: 600 }}
            onClick={handleDeleteLandlord}
          >
            {t('cms.form.button.delete')}
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon type="exclamation-circle" className="delete-modal-icon" />
          <span className="delete-modal-desc">
            {t('cms.property_management.delete_landlord.modal.tip')}
          </span>
        </div>
      </Modal>
    </div>
  );
};

LandlordHeader.propTypes = {
  landlord: PropTypes.object,
  getLandlord: PropTypes.func.isRequired,
};

LandlordHeader.defaultProps = {
  landlord: {},
  getLandlord: () => {},
};

export default LandlordHeader;
